import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { OUItem } from '@/api/types';
import type { OuNode } from './ouTree';

interface OuTreeCardProps {
  node: OuNode;
  depth: number;
  isCollapsed: (id: string) => boolean;
  onToggle: (id: string) => void;
  renderContent: (ou: OUItem) => ReactNode;
}

/**
 * Árbol jerárquico de OUs con el look visual de Analytics → ONA (fila tipo tarjeta,
 * chevron circular rotante, indentación por línea vertical), pero genérico: recibe
 * el contenido de cada fila vía `renderContent` en vez de estar acoplado a métricas.
 */
export function OuTreeCard({ node, depth, isCollapsed, onToggle, renderContent }: OuTreeCardProps) {
  const { t } = useTranslation();
  const { ou, children } = node;
  const hasChildren = children.length > 0;
  const collapsed = hasChildren && isCollapsed(ou.id);

  return (
    <div style={{ marginLeft: depth * 20 }}>
      <div
        className={`ona2-row${hasChildren ? ' clickable' : ''}${hasChildren && !collapsed ? ' open' : ''}`}
        onClick={() => hasChildren && onToggle(ou.id)}
        role={hasChildren ? 'button' : undefined}
        aria-expanded={hasChildren ? !collapsed : undefined}
      >
        <div className="ona2-name-row">
          <span className="ona2-name">{ou.name}</span>
        </div>

        <div className="ona2-row-actions" onClick={rowActionsClick => rowActionsClick.stopPropagation()}>
          {renderContent(ou)}
        </div>

        {hasChildren && (
          <span
            className={`ona2-chevron${collapsed ? '' : ' open'}`}
            aria-label={collapsed ? t('admin.ouExpand') : t('admin.ouCollapse')}
          >
            &rsaquo;
          </span>
        )}
      </div>

      {hasChildren && !collapsed && (
        <div className="ona2-children">
          {children.map(childNode => (
            <OuTreeCard
              key={childNode.ou.id}
              node={childNode}
              depth={depth + 1}
              isCollapsed={isCollapsed}
              onToggle={onToggle}
              renderContent={renderContent}
            />
          ))}
        </div>
      )}
    </div>
  );
}
